import { subclass, property } from "@arcgis/core/core/accessorSupport/decorators";
import PortalItem from '@arcgis/core/portal/PortalItem';
import { messageBundle, tsx } from "@arcgis/core/widgets/support/widget";
import Widget from "@arcgis/core/widgets/Widget";
import { UserSession } from '@esri/arcgis-rest-auth';
import { createItem } from '@esri/arcgis-rest-portal';
import JSZip from 'jszip';
import { VNode } from 'src/interfaces/interfaces';
import { EAppTemplateType } from 'templates-common-library/baseClasses/CompatibilityChecker';

import t9nAppMessages from "../../t9n/App/App.json";
import ContentAreaViewModel from '../ui/ContentArea/ContentAreaViewModel';
import { APPSTUDIO_MODAL_STYLE } from '../utils/shadowStyles';
import { logAppStudioCreation } from '../utils/telemetryUtils';
import { applyShadowStyle } from '../utils/utils';
import "./AppStudioModal/AppStudioModal.scss";

export interface AppStudioModalProperties extends __esri.WidgetProperties {
  appInfo: __esri.PortalItem;
  orgUrl: string;
  contentAreaVM: ContentAreaViewModel;
}

 export enum EAPP_STUDIO_COMPATIBLE {
  AttachmentViewer  = EAppTemplateType.AttachmentViewer,
  Nearby            = EAppTemplateType.Nearby,
  ZoneLookup        = EAppTemplateType.ZoneLookup,
  Sidebar           = EAppTemplateType.Sidebar,
}

const APP_STUDIO_URLS = {
  [EAPP_STUDIO_COMPATIBLE.AttachmentViewer]:     "b3e2a44dedc94a42aed24cf9c1ac8651",
  [EAPP_STUDIO_COMPATIBLE.Nearby]:               "910cc7606347434c81a83ce0873e647d",
  [EAPP_STUDIO_COMPATIBLE.ZoneLookup]:           "24424c8d3b254e99b9600c8257c554d9",
  [EAPP_STUDIO_COMPATIBLE.Sidebar]:              "2eb3e55c359b47ddbbee78fed07002c7"
}

const APPSTUDIO_INFO_URL = "https://doc.arcgis.com/en/appstudio/configure-apps/guidedtour.htm";

@subclass("Landing.widgets.AppStudioModal")
export default class AppStudioModal extends Widget {

  //--------------------------------------------------------------------------
  //
  //  Properties
  //
  //--------------------------------------------------------------------------

  @property()
  appInfo: __esri.PortalItem;

  @property()
  orgUrl: string;

  @property()
  contentAreaVM: ContentAreaViewModel;

  @property()
  @messageBundle("dist/assets/t9n/App/App")
  appMessages: typeof t9nAppMessages;

  @property()
  modalVisible: boolean = false;

  @property()
  _modalDOMContent: VNode[] = [];
  @property()
  _canDelete: boolean;
  @property()
  _isProcessing: boolean = false;

  @property()
  _modalState: "createConfirmation" | "processing" | "success" | "failure" | "alreadyExists" | "notExistsAnymore" = "createConfirmation";

  @property()
  appStudioCompanionId: string;

  private _modal: any;
  private _isFirstOpen: boolean = true;
  private _failMsg: string;
  private _appData: any;
  private _templateType: EAppTemplateType;
  //--------------------------------------------------------------------------
  //
  //  Lifecycle Methods
  //
  //--------------------------------------------------------------------------

  constructor(props: AppStudioModalProperties) {
    super(props);
  }

  render() {
    const { confirmCreateApp } = this.appMessages.myApps.appStudio;
    return (
      <calcite-modal
        active={this.modalVisible}
        background-color="white"
        scale="m"
        width="s"
        afterCreate={(modal: HTMLCalciteModalElement) => {
          this._modal = modal;
          modal.addEventListener("calciteModalClose", () => {
            this.hideModal();
          });
          applyShadowStyle(APPSTUDIO_MODAL_STYLE, modal);
        }}
      >
        <h1 slot="header">{confirmCreateApp.title}</h1>

        { this._renderContentArea()}
      </calcite-modal>
    );
  }

  //--------------------------------------------------------------------------
  //
  //  Public Methods
  //
  //--------------------------------------------------------------------------
  async showModal(): Promise<void> {
    if (this._isFirstOpen) {
      const clickDisable = (e) => {
        e?.preventDefault();
        e?.stopImmediatePropagation();
      };

      const scrim = this?._modal?.shadowRoot?.querySelector(".scrim");
      const innerScrim = scrim?.shadowRoot?.querySelector(".scrim");

      scrim?.addEventListener("click", clickDisable);
      innerScrim?.addEventListener("click", clickDisable);

      this._isFirstOpen = false;
    }
    this.modalVisible = true;
    this._modalState = "processing";
    this._appData = await this.appInfo.fetchData("json");
    const companionId = this._appData.appStudioCompanion;
    if (companionId == null) {
      this._modalState = "createConfirmation";
    } else {
      try {
        const companionApp = new PortalItem({ id: companionId });
        await companionApp.load();
        this.appStudioCompanionId = companionId;
        this._modalState = "alreadyExists";
      } catch (err) {
        this._appData.appStudioCompanion = null;
        await this.appInfo.update({ data: this._appData });
        this._modalState = "notExistsAnymore";
      }
    }
  }

  hideModal(): void {
    this.modalVisible = false;
  }

  //--------------------------------------------------------------------------
  //
  //  Private Methods
  //
  //--------------------------------------------------------------------------

  private _renderContentArea(): VNode | VNode[] {
    switch (this._modalState) {
      case "createConfirmation":
        return this._renderCreateConfirmation();
      case "processing":
        return <div slot="content"><calcite-loader active></calcite-loader></div>;
      case "success":
        return this._renderSuccessDialog();
      case "failure":
        return this._renderFailureDialog();
      case "alreadyExists":
        return this._renderAlreadyExistsDialog();
      case "notExistsAnymore":
        return this._renderDoesNotExistAnymore();
    }
  }

  private _renderCreateConfirmation(): VNode[] {
    const { confirmCreateApp, cancel, confirm } = this.appMessages.myApps.appStudio;
    const { body1, body2, body3 } = confirmCreateApp;
    return [
      <div slot="content" key="createDialog">
        {body1} <a href={APPSTUDIO_INFO_URL} target="_blank">{body2}</a> {body3}
      </div>,
      <div slot="secondary" key="createBtn2">
        <calcite-button
          key="appStudio_cancelBtn"
          width="full"
          appearance="outline"
          alignment="center"
          scale="l"
          onclick={this.hideModal.bind(this)}
        >
          {cancel}
        </calcite-button>
      </div>,
      <div slot="primary" key="createBtn1">
        <calcite-button
          key="appStudio_deleteBtn"
          width="full"
          appearance="solid"
          alignment="center"
          scale="l"
          loading={this._isProcessing}
          disabled={this._isProcessing}
          onclick={this._performAppStudioCreate.bind(this)}
        >
          {confirm}
        </calcite-button>
      </div>
    ]
  }

  private _renderSuccessDialog() {
    const { createSuccess, close } = this.appMessages.myApps.appStudio;
    const { body, button } = createSuccess;
    return [
      <div slot="content">
        <div>{body}</div>
      </div>,
      <div slot="secondary">
        <calcite-button
          key="appStudio_cancelBtn"
          width="full"
          appearance="outline"
          alignment="center"
          scale="l"
          onclick={this.hideModal.bind(this)}
        >
          {close}
        </calcite-button>
      </div>,
      <div slot="primary">
        <calcite-button
          key="appStudio_openBtn"
          width="full"
          appearance="solid"
          alignment="center"
          scale="l"
          onclick={() => {
            window.open(`${this.contentAreaVM.publishAppVM.orgUrl}/home/item.html?id=${this.appStudioCompanionId}`);
            this.hideModal();
          }}
        >
          {button}
        </calcite-button>
      </div>
    ]
  }

  private _renderFailureDialog(): VNode[] {
    const { fail_1, fail_2, confirm } = this.appMessages.myApps.appStudio;
    return [
      <div slot="content" key="failureDialog">
        <p>{fail_1}</p>
        <p>{this._failMsg}</p>
        <p>{fail_2} <a href="https://community.esri.com/t5/arcgis-instant-apps-questions/bd-p/arcgis-instant-apps-questions">Instant Apps</a></p>
      </div>,
      <div slot="primary" key="failureBtn1">
        <calcite-button
          key="appStudio_ConfirmFailBtn"
          width="full"
          appearance="solid"
          alignment="center"
          scale="l"
          onclick={this.hideModal.bind(this)}
        >
          {confirm}
        </calcite-button>
      </div>
    ]
  }

  private _renderAlreadyExistsDialog() {
    const { appAlreadyExist, close } = this.appMessages.myApps.appStudio;
    const { body1, body2, body3, button } = appAlreadyExist;
    return [
      <div slot="content" key="alreadyExists">
        {body1} <a href={APPSTUDIO_INFO_URL} target="_blank">{body2}</a> {body3}
      </div>,
      <div slot="secondary" key="alreadyExistsBtn2">
        <calcite-button
          width="full"
          appearance="outline"
          alignment="center"
          scale="l"
          onclick={this.hideModal.bind(this)}
        >
          {close}
        </calcite-button>
      </div>,
      <div slot="primary" key="alreadyExistsBtn1">
        <calcite-button
          key="appStudio_openBtn"
          width="full"
          appearance="solid"
          alignment="center"
          scale="l"
          onclick={() => {
            window.open(`${this.contentAreaVM.publishAppVM.orgUrl}/home/item.html?id=${this.appStudioCompanionId}`);
            this.hideModal();
          }}
        >
          {button}
        </calcite-button>
      </div>
    ]
  }

  private _renderDoesNotExistAnymore(): VNode[] {
    const { appNoLongerExists, cancel, confirm } = this.appMessages.myApps.appStudio;
    const { body1, body2, body3 } = appNoLongerExists;
    return [
      <div slot="content" key="DNEAnymore">
        {body1} <a href={APPSTUDIO_INFO_URL} target="_blank">{body2}</a> {body3}
      </div>,
      <div slot="secondary" key="DNEAnymoreBtn2">
        <calcite-button
          width="full"
          appearance="outline"
          alignment="center"
          scale="l"
          onclick={this.hideModal.bind(this)}
        >
          {cancel}
        </calcite-button>
      </div>,
      <div slot="primary" key="DNEAnymoreBtn1">
        <calcite-button
          width="full"
          appearance="solid"
          alignment="center"
          scale="l"
          loading={this._isProcessing}
          disabled={this._isProcessing}
          onclick={this._performAppStudioCreate.bind(this)}
        >
          {confirm}
        </calcite-button>
      </div>
    ]
  }

  private async _performAppStudioCreate(): Promise<void> {
    try {
      this._modalState = "processing";
      this.scheduleRender();
      const { id, title, url, description, snippet } = this.appInfo;

      let instantAppTitle = title;
      let instantAppId = id;
      const appStudioUrl = `https://appstudio.maps.arcgis.com/sharing/rest/content/items/${this._decideAppStudioId(url)}`;
      const new_zip = new JSZip();
      const response = await Promise.all([
        fetch(`${appStudioUrl}?f=json`),
        fetch(`${appStudioUrl}/data`),
        fetch(`${appStudioUrl}/info/thumbnail/thumbnail.png`),
      ]);
      const jsonInfo = await response[0].json();
      const dataInfo = await response[1].blob();
      const thumbnailInfo = await response[2].blob();
      const zip = await new_zip.loadAsync(dataInfo);
      const appInfoJSONStr = await zip.file("appinfo.json").async("string");

      const jsonRep = JSON.parse(appInfoJSONStr);
      jsonRep.properties.galleryMapsQuery = `id:${instantAppId}`;
      jsonRep.properties.portalUrl = (this.contentAreaVM.portal as any).portalUrl.replace("sharing/rest/", "");
      const full = JSON.stringify(jsonRep);

      await zip.file("appinfo.json", full);

      const zippedBlob = await zip.generateAsync({ type: "blob" });
      const zippedFile = new File([zippedBlob], instantAppId.substring(0, 10) + new Date().toISOString() + ".zip");

      /** Use Instant App Value if exists, otherwise default to AppStudio Item's Value */
      const handleValPriority = (instantAppVal, appStudioVal) => 
        instantAppVal === "" || instantAppVal == null ? appStudioVal : instantAppVal;

      const creationResult = await createItem({
        item: {
          title: instantAppTitle,
          type:               jsonInfo?.type,
          categories:         jsonInfo?.categories,
          culture:            jsonInfo?.culture,
          description:        handleValPriority(description, jsonInfo?.description),
          documentation:      jsonInfo?.documentation,
          extent:             jsonInfo?.extent,
          properties:         jsonInfo?.properties,
          snippet:            handleValPriority(snippet, jsonInfo?.snippet),
          spatialReference:   jsonInfo?.spatialReference,
          tags:               jsonInfo?.tags,
          typeKeywords:       jsonInfo?.typeKeywords,
          accessInformation:  jsonInfo?.accessInformation,
          licenseInfo:        jsonInfo?.licenseInfo,
          url:                jsonInfo?.url,
        },
        hideToken: true,
        file: zippedFile,

        authentication: new UserSession({
          portal: (this.contentAreaVM.portal as any).portalUrl,
          token: (this.contentAreaVM.portal as any).credential.token,
          clientId: "arcgisWebApps"
        })
      });

      this._isProcessing = false;
      if (creationResult.success) {
        this.appStudioCompanionId = creationResult.id;
        this._appData.appStudioCompanion = creationResult.id;
        /** Populate Instant App's data with new companion AppStudio app's id */
        const updateRes = await this.appInfo.update({ data: this._appData });
        /** Update new companion AppStudio app's thumbnail */
        let newAppStudioItem = new PortalItem({
          id: this.appStudioCompanionId
        });
        const updateThumbnailRes = await newAppStudioItem.updateThumbnail({ thumbnail: thumbnailInfo, 
          filename: "thumbnail.png" 
        });
        this._modalState = "success";
        logAppStudioCreation(
          this.contentAreaVM.telemetry,
          this._templateType,
          true,
          `${(this.contentAreaVM.portal as any).portalUrl.replace("sharing/rest/", "home/item.html?id=")}${this.appStudioCompanionId}`
        );
      } else {
        const errMsg = `${this.appMessages.myApps.appStudio.creationError} ${JSON.stringify(creationResult)}`;
        console.error(errMsg);
        throw new Error(errMsg);
      }
    } catch (err) {
      this._modalState = "failure";
      this._failMsg = err;
      logAppStudioCreation(
        this.contentAreaVM.telemetry,
        this._templateType,
        false,
        err?.toString()
      );
    }
  }

  private _decideAppStudioId(url: string): string {
    if (url.includes(EAppTemplateType.AttachmentViewer)) {
      this._templateType = EAppTemplateType.AttachmentViewer;
      return APP_STUDIO_URLS[EAppTemplateType.AttachmentViewer];

    } else if (url.includes(EAppTemplateType.Nearby)) {
      this._templateType = EAppTemplateType.Nearby;
      return APP_STUDIO_URLS[EAppTemplateType.Nearby];

    } else if (url.includes(EAppTemplateType.ZoneLookup)) {
      this._templateType = EAppTemplateType.ZoneLookup;
      return APP_STUDIO_URLS[EAppTemplateType.ZoneLookup];

    } else if (url.includes(EAppTemplateType.Sidebar)) {
      this._templateType = EAppTemplateType.Sidebar;
      return APP_STUDIO_URLS[EAppTemplateType.Sidebar];
    }
    else {
      const errMsg = `${this.appMessages.myApps.appStudio.notSupported}, ${url}`;
      this._templateType = null;
      console.error(errMsg);
      throw new Error(errMsg);
    }
  }
}